import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Mailbox, Loader2, Search, X, CheckCircle2, FlaskConical, AlertTriangle,
    Paperclip, Clock, User as UserIcon, Building2, Send, BarChart3, Download,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const STATUS_META = {
    sent: { label: "Versendet", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", Icon: CheckCircle2 },
    mock: { label: "Simuliert", cls: "bg-amber-100 text-amber-900 border-amber-300", Icon: FlaskConical },
    failed: { label: "Fehlgeschlagen", cls: "bg-red-100 text-red-900 border-red-300", Icon: AlertTriangle },
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("de-DE", {
            day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
};

function StatusBadge({ status, testid }) {
    const m = STATUS_META[status] || { label: status, cls: "bg-gray-100 text-gray-700 border-gray-300", Icon: Clock };
    const { Icon } = m;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm border ${m.cls}`} data-testid={testid}>
            <Icon className="w-3 h-3" /> {m.label}
        </span>
    );
}

export default function SystemEmailsPage() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState({ items: [], total: 0, counts: {} });
    const [busy, setBusy] = useState(true);
    const [statusFilter, setStatusFilter] = useState("all");
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(null);
    const [detailBusy, setDetailBusy] = useState(false);
    const [resending, setResending] = useState(false);
    const [report, setReport] = useState(null);
    const [reportOpen, setReportOpen] = useState(false);
    const [reportBusy, setReportBusy] = useState(false);
    const [reportWeeks, setReportWeeks] = useState(8);
    const [exporting, setExporting] = useState(false);

    const canAccess = user && (
        user.role === "b2b" || user.role === "admin" ||
        (user.role === "zweiradverkaeufer" && user.employee_function === "filialleiter")
    );

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const params = {};
            if (statusFilter !== "all") params.status = statusFilter;
            if (q.trim()) params.q = q.trim();
            const r = await api.get("/dealer/emails", { params });
            setData(r.data);
        } catch (e) { console.error(e); }
        finally { setBusy(false); }
    }, [statusFilter, q]);

    useEffect(() => {
        if (loading) return;
        if (!canAccess) { navigate("/"); return; }
        load();
    }, [loading, canAccess, navigate, load]);

    const openDetail = async (item) => {
        setSelected(item);
        setDetailBusy(true);
        try {
            const r = await api.get(`/dealer/emails/${item.id}`);
            setSelected(r.data);
        } catch (e) { console.error(e); }
        finally { setDetailBusy(false); }
    };

    const resend = async (item) => {
        setResending(true);
        try {
            await api.post(`/dealer/emails/${item.id}/resend`);
            toast.success("E-Mail erneut versendet");
            setSelected(null);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erneut senden fehlgeschlagen");
        } finally { setResending(false); }
    };

    const loadReport = async (weeks) => {
        setReportBusy(true);
        try {
            const r = await api.get("/dealer/emails/weekly-report", { params: { weeks } });
            setReport(r.data);
        } catch (e) { console.error(e); }
        finally { setReportBusy(false); }
    };

    const toggleReport = async () => {
        const next = !reportOpen;
        setReportOpen(next);
        if (next && !report) loadReport(reportWeeks);
    };

    const changeReportWeeks = (weeks) => {
        setReportWeeks(weeks);
        loadReport(weeks);
    };

    const exportReportCsv = async () => {
        setExporting(true);
        try {
            const r = await api.get("/dealer/emails/weekly-report.csv", {
                params: { weeks: reportWeeks }, responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `standort-postfach-wochenreport_${reportWeeks}w.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CSV exportiert");
        } catch (e) {
            toast.error("Export fehlgeschlagen");
        } finally { setExporting(false); }
    };

    if (loading || !user) return <div className="p-12">Lädt…</div>;
    const counts = data.counts || {};
    const FILTERS = [
        ["all", "Alle", counts.total ?? 0],
        ["sent", "Versendet", counts.sent ?? 0],
        ["mock", "Simuliert", counts.mock ?? 0],
        ["failed", "Fehlgeschlagen", counts.failed ?? 0],
    ];

    return (
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12" data-testid="system-emails-page">
            <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-black tracking-tight text-[#111827] flex items-center gap-2">
                        <Mailbox className="w-7 h-7 text-[#FFCC00]" /> Standort-Postfach
                    </h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Protokoll aller automatisch versendeten System-E-Mails Ihrer Organisation.
                        Sichtbar für Filialleiter und Konto-Inhaber.
                    </p>
                </div>
                <button
                    onClick={toggleReport}
                    className={`inline-flex items-center gap-2 rounded-sm px-4 py-2 text-sm font-bold shrink-0 transition-colors ${reportOpen ? "bg-[#111827] text-white" : "border border-gray-300 text-[#111827] hover:border-[#111827]"}`}
                    data-testid="system-emails-report-toggle"
                >
                    <BarChart3 className="w-4 h-4" /> Wochen-Report
                </button>
            </div>

            {reportOpen && (
                <div className="mb-6 bg-white border border-gray-200 rounded-sm p-4" data-testid="system-emails-report-panel">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                            Versand pro Kalenderwoche & Standort (letzte {report?.weeks ?? reportWeeks} Wochen)
                        </div>
                        <div className="flex items-center gap-2">
                            <select
                                value={reportWeeks}
                                onChange={(e) => changeReportWeeks(Number(e.target.value))}
                                className="h-8 rounded-sm border border-gray-300 px-2 text-xs font-bold"
                                data-testid="system-emails-report-weeks"
                            >
                                <option value={4}>4 Wochen</option>
                                <option value={8}>8 Wochen</option>
                                <option value={12}>12 Wochen</option>
                            </select>
                            <button
                                onClick={exportReportCsv}
                                disabled={exporting || reportBusy}
                                className="inline-flex items-center gap-1.5 rounded-sm border border-gray-300 px-3 py-1.5 text-xs font-bold hover:border-[#111827] disabled:opacity-60"
                                data-testid="system-emails-report-export"
                            >
                                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} CSV
                            </button>
                        </div>
                    </div>
                    {reportBusy ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                    ) : !report || report.items.length === 0 ? (
                        <div className="text-sm text-gray-400 py-4 text-center" data-testid="system-emails-report-empty">Noch keine Daten.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="system-emails-report-table">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                                        <th className="py-2 pr-4">Woche</th>
                                        <th className="py-2 pr-4">Standort</th>
                                        <th className="py-2 pr-4 text-right text-emerald-700">Versendet</th>
                                        <th className="py-2 pr-4 text-right text-amber-700">Simuliert</th>
                                        <th className="py-2 pr-4 text-right text-red-700">Fehlgeschlagen</th>
                                        <th className="py-2 text-right">Gesamt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.items.map((r, i) => (
                                        <tr key={`${r.week}-${r.branch}-${i}`} className="border-b border-gray-50" data-testid="system-emails-report-row">
                                            <td className="py-2 pr-4 font-mono-num">{r.week}</td>
                                            <td className="py-2 pr-4 font-mono">{r.branch}</td>
                                            <td className="py-2 pr-4 text-right font-mono-num text-emerald-700">{r.sent}</td>
                                            <td className="py-2 pr-4 text-right font-mono-num text-amber-700">{r.mock}</td>
                                            <td className="py-2 pr-4 text-right font-mono-num text-red-700">{r.failed}</td>
                                            <td className="py-2 text-right font-mono-num font-bold">{r.total}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {FILTERS.map(([k, label, n]) => (
                    <button
                        key={k}
                        onClick={() => setStatusFilter(k)}
                        className={`text-left bg-white border rounded-sm p-4 transition-colors ${statusFilter === k ? "border-[#111827] ring-1 ring-[#111827]" : "border-gray-200 hover:border-gray-400"}`}
                        data-testid={`system-emails-kpi-${k}`}
                    >
                        <div className="text-3xl font-black font-mono-num text-[#111827]">{n}</div>
                        <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-1">{label}</div>
                    </button>
                ))}
            </div>

            <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                        placeholder="Nach Betreff oder Empfänger suchen…"
                        className="w-full rounded-sm border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-[#111827] focus:outline-none"
                        data-testid="system-emails-search"
                    />
                </div>
                <button onClick={load} className="bg-[#111827] text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-black" data-testid="system-emails-search-btn">
                    Suchen
                </button>
            </div>

            {busy ? (
                <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
            ) : (data.items || []).length === 0 ? (
                <div className="text-center text-gray-500 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="system-emails-empty">
                    Keine E-Mails gefunden.
                </div>
            ) : (
                <div className="space-y-2" data-testid="system-emails-list">
                    {data.items.map((m) => (
                        <button
                            key={m.id}
                            onClick={() => openDetail(m)}
                            className="w-full text-left bg-white border border-gray-200 rounded-sm p-4 hover:border-[#111827] transition-colors"
                            data-testid={`system-email-${m.id}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <StatusBadge status={m.status} testid={`system-email-status-${m.id}`} />
                                        {m.has_attachments && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-gray-500"><Paperclip className="w-3 h-3" /> Anhang</span>}
                                    </div>
                                    <div className="font-bold text-[#111827] mt-1 truncate">{m.subject || "(ohne Betreff)"}</div>
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mt-1">
                                        <span className="inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {m.to}</span>
                                        {m.triggered_by_name && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {m.triggered_by_name}</span>}
                                        {m.branch_site_code && <span className="font-mono">{m.branch_site_code}</span>}
                                    </div>
                                </div>
                                <div className="text-xs text-gray-400 whitespace-nowrap inline-flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {fmtDate(m.created_at)}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {selected && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="system-email-detail-modal">
                    <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3 sticky top-0 bg-white">
                            <div className="min-w-0 pr-3">
                                <div className="flex items-center gap-2"><StatusBadge status={selected.status} testid="system-email-detail-status" /></div>
                                <h2 className="font-black text-base text-[#111827] mt-1 truncate">{selected.subject || "(ohne Betreff)"}</h2>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700 shrink-0" data-testid="system-email-detail-close"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <dl className="grid grid-cols-3 gap-y-1 text-sm">
                                <dt className="text-gray-500">Empfänger</dt><dd className="col-span-2 font-medium break-all">{selected.to}</dd>
                                {selected.cc?.length > 0 && (<><dt className="text-gray-500">CC</dt><dd className="col-span-2 break-all">{selected.cc.join(", ")}</dd></>)}
                                <dt className="text-gray-500">Ausgelöst von</dt><dd className="col-span-2">{selected.triggered_by_name || "System"} {selected.triggered_by_role ? `(${selected.triggered_by_role})` : ""}</dd>
                                {selected.branch_site_code && (<><dt className="text-gray-500">Standort</dt><dd className="col-span-2 font-mono">{selected.branch_site_code}</dd></>)}
                                <dt className="text-gray-500">Zeitpunkt</dt><dd className="col-span-2">{fmtDate(selected.created_at)}</dd>
                                {selected.provider_message_id && (<><dt className="text-gray-500">Provider-ID</dt><dd className="col-span-2 font-mono text-xs break-all">{selected.provider_message_id}</dd></>)}
                                {selected.error && (<><dt className="text-gray-500">Fehler</dt><dd className="col-span-2 text-red-700">{selected.error}</dd></>)}
                            </dl>
                            <div className="border-t border-gray-200 pt-3">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Inhalt</div>
                                {detailBusy ? (
                                    <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                                ) : selected.body_html ? (
                                    <div className="border border-gray-200 rounded-sm overflow-hidden">
                                        <iframe title="E-Mail-Vorschau" srcDoc={selected.body_html} className="w-full h-[420px] bg-white" sandbox="" data-testid="system-email-detail-body" />
                                    </div>
                                ) : (
                                    <div className="text-sm text-gray-400">Kein Inhalt verfügbar.</div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3 sticky bottom-0 bg-white">
                            <button onClick={() => setSelected(null)} className="rounded-sm border border-gray-300 px-4 py-2 text-sm font-bold hover:bg-gray-50" data-testid="system-email-detail-close-btn">Schließen</button>
                            <button onClick={() => resend(selected)} disabled={resending} className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] rounded-sm px-4 py-2 text-sm font-bold hover:brightness-95 disabled:opacity-60" data-testid="system-email-resend-btn">
                                {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Erneut senden
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
